import React, { useEffect, useState, useRef } from "react";
import Header from "../components/header";
import Footer from "../components/Footer";
import BackToTop from "../components/BackToTop";
import WhatsappButton from "../components/WhatsappButton";
import { useDarkMode } from "../Functions/DarkMode";
import { toast } from "react-toastify";
import { sendContactEnquiry } from "../Functions/ContactEnquiry";
import { Helmet } from "react-helmet-async";

const Contact = () => {
    const { darkMode } = useDarkMode();
    const [formData, setFormData] = useState({
        name: '',
        email: '',
        phone: '',
        message: ''
    });
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [submissionSuccess, setSubmissionSuccess] = useState(false);
    
    const [validation, setValidation] = useState({
        email: true,
        phone: true
    });

    const [validationMessages, setValidationMessages] = useState({
        email: '',
        phone: ''
    });
    
    const formRef = useRef(null);

    useEffect(() => {
        const link = document.createElement('link');
        link.rel = 'stylesheet';
        link.href = '/assets/css/Contact.css';
        document.head.appendChild(link);
        
        return () => {
            if (document.head.contains(link)) {
                document.head.removeChild(link);
            }
        };
    }, []);

    useEffect(() => {
        if (darkMode) {
            document.body.setAttribute('data-theme', 'dark');
        } else {
            document.body.removeAttribute('data-theme');
        }
    }, [darkMode]);

    const validateEmail = (email) => {
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        return emailRegex.test(email);
    };

    const validatePhone = (phone) => {
        const phoneRegex = /^[0-9\s\-\+\(\)]{6,15}$/;
        return phoneRegex.test(phone);
    };

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: value
        }));
        
        if (name === 'email') {
            const isValid = value === '' || validateEmail(value);
            setValidation(prev => ({ ...prev, email: isValid }));
            setValidationMessages(prev => ({ 
                ...prev, 
                email: isValid ? '' : 'Please enter a valid email address'
            }));
        } else if (name === 'phone') {
            const isValid = value === '' || validatePhone(value);
            setValidation(prev => ({ ...prev, phone: isValid }));
            setValidationMessages(prev => ({ 
                ...prev, 
                phone: isValid ? '' : 'Please enter a valid phone number'
            }));
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        
        // Check if all fields are filled
        if (!formData.name || !formData.email || !formData.phone || !formData.message) {
            toast.error("Please fill in all required fields");
            return;
        }
        
        const isEmailValid = validateEmail(formData.email);
        const isPhoneValid = validatePhone(formData.phone);
        
        setValidation({
            email: isEmailValid,
            phone: isPhoneValid
        });
        
        setValidationMessages({
            email: isEmailValid ? '' : 'Please enter a valid email address',
            phone: isPhoneValid ? '' : 'Please enter a valid phone number'
        });
        
        if (isEmailValid && isPhoneValid) {
            setIsSubmitting(true);
            
            try {
                const enquiryData = {
                    ...formData,
                    submittedAt: new Date().toISOString(),
                    source: 'contact_page'
                };
                
                const result = await sendContactEnquiry(enquiryData);
                
                if (result.success) {
                    console.log("Contact form submitted to database:", enquiryData);
                    
                    const formElement = formRef.current;
                    const formData = new FormData(formElement);
                    
                    formData.append('_subject', 'New Contact Form Submission - LSQ Stays');
                    
                    const emailResponse = await fetch('https://formsubmit.co/info@lsqstays.com', {
                        method: 'POST',
                        body: formData,
                        headers: {
                            'Accept': 'application/json'
                        }
                    });
                    
                    if (!emailResponse.ok) {
                        console.warn("Email delivery may have failed:", emailResponse.statusText);
                    }
                    
                    toast.success("Thank you for your message! We'll get back to you shortly.");
                    
                    setFormData({
                        name: '',
                        email: '',
                        phone: '',
                        message: ''
                    });
                    
                    setSubmissionSuccess(true);
                } else {
                    throw new Error(result.error || "Failed to submit form");
                }
            } catch (error) {
                console.error("Error submitting form:", error);
                toast.error("There was an error sending your message. Please try again.");
            } finally {
                setIsSubmitting(false);
            }
        }
    };

    return (
        <div className="lsq-site-container">
            <Helmet>
                <title>Contact LSQ Stays | Get In Touch For Luxury Vacation Rentals</title>
                <meta name="description" content="Have questions about LSQ Stays luxury vacation rentals? Contact our dedicated team today for inquiries, booking assistance, or any questions about our properties." />
                <link rel="canonical" href="https://www.lsqstays.in/contact" />
                <script type="application/ld+json">
                {JSON.stringify({
                    "@context": "https://schema.org",
                    "@type": "ContactPage",
                    "mainEntity": {
                        "@type": "Organization",
                        "name": "LSQ Stays",
                        "url": "https://www.lsqstays.in",
                        "logo": {
                            "@type": "ImageObject",
                            "url": "https://www.lsqstays.in/assets/Images/logo.png",
                            "width": 112,
                            "height": 112,
                            "contentUrl": "https://www.lsqstays.in/assets/Images/logo.png"
                        },
                        "image": "https://www.lsqstays.in/assets/Images/logo.png",
                        "contactPoint": [
                            {
                                "@type": "ContactPoint",
                                "telephone": "+91-89288-36738",
                                "contactType": "customer service",
                                "email": "info@lsqstays.com",
                                "availableLanguage": ["English", "Hindi"]
                            }
                        ],
                        "address": {
                            "@type": "PostalAddress",
                            "addressLocality": "Mumbai",
                            "addressRegion": "Maharashtra",
                            "addressCountry": "India"
                        },
                        "sameAs": [
                            "https://www.instagram.com/lsqstays/",
                            "https://www.linkedin.com"
                        ]
                    }
                })}
                </script>
            </Helmet>
            <Header currentPage="contact" />
            
            <div className={`contact-page ${darkMode ? 'dark-mode' : ''}`}>
                <div className="contact-content">
                    <header className="contact-header">
                        <div className="container">
                            <h1>Have Questions? Contact Us Today</h1>
                            <p>We're happy to assist you with all your needs! Whether you want to inquire about available villas, need booking assistance, or just have a question, don't hesitate to contact us. We'll ensure that your experience with LSQ Stays is a memorable one.</p>
                        </div>
                    </header>

                    <section className="contact-methods">
                        <div className="container">
                            <div className="contact-cards">
                                <div className="contact-card">
                                    <div className="icon-circle light-blue">
                                        <i className="fas fa-headset"></i>
                                    </div>
                                    <h2>Call us</h2>
                                    <p>Reach Us Anytime, We're Just a Call Away!</p>
                                    <a href="tel:+918850544012" className="contact-button phone">
                                        <i className="fas fa-phone"></i> +91 89288 36738
                                    </a>
                                </div>

                                <div className="contact-card">
                                    <div className="icon-circle light-pink">
                                        <i className="fas fa-envelope"></i>
                                    </div>
                                    <h2>Email us</h2>
                                    <p>Send Us a Message, We're Here to Help!</p>
                                    <a href="mailto:info@lsqstays.com" className="contact-link">
                                        <i className="fas fa-envelope"></i> info@lsqstays.com
                                    </a>
                                </div>

                                <div className="contact-card">
                                    <div className="icon-circle light-orange">
                                        <i className="fas fa-globe"></i>
                                    </div>
                                    <h2>Social media</h2>
                                    <div className="social-icons">
                                        <a href="https://www.airbnb.com" target="_blank" rel="noopener noreferrer" className="social-icon airbnb">
                                            <i className="fab fa-airbnb"></i>
                                        </a>
                                        <a href="hthttps://www.instagram.com/lsqstays/" target="_blank" rel="noopener noreferrer" className="social-icon instagram">
                                            <i className="fab fa-instagram"></i>
                                        </a>
                                        <a href="https://www.linkedin.com" target="_blank" rel="noopener noreferrer" className="social-icon linkedin">
                                            <i className="fab fa-linkedin-in"></i>
                                        </a>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </section>

                    <section className="contact-form-section">
                        <div className="container">
                            <div className="form-container">
                                <div className="illustration">
                                    <img src="/assets/Images/logo.png" alt="Vacation illustration" />
                                </div>
                                <div className="form-wrapper">
                                    <h2>Get in Touch with Us</h2>
                                    {submissionSuccess ? (
                                        <div className="submission-success">
                                            <div className="success-icon">
                                                <i className="fas fa-check-circle"></i>
                                            </div>
                                            <h3>Thank You!</h3>
                                            <p>Your message has been sent successfully. We'll get back to you shortly.</p>
                                            <button 
                                                className="new-message-btn"
                                                onClick={() => setSubmissionSuccess(false)}
                                            >
                                                Send Another Message
                                            </button>
                                        </div>
                                    ) : (
                                        <form id="contactForm" ref={formRef} onSubmit={handleSubmit}>
                                            <input type="hidden" name="_captcha" value="false" />
                                            <input type="hidden" name="_template" value="table" />
                                            <input type="hidden" name="_next" value={window.location.href} />
                                            
                                            <div className="form-row">
                                                <div className="form-group">
                                                    <label htmlFor="name">Your name <span className="required">*</span></label>
                                                    <input 
                                                        type="text" 
                                                        id="name" 
                                                        name="name" 
                                                        value={formData.name}
                                                        onChange={handleChange}
                                                        required 
                                                    />
                                                </div>
                                                <div className="form-group">
                                                    <label htmlFor="email">Email address <span className="required">*</span></label>
                                                    <input 
                                                        type="email" 
                                                        id="email" 
                                                        name="email" 
                                                        value={formData.email}
                                                        onChange={handleChange}
                                                        required 
                                                        className={!validation.email ? 'is-invalid' : ''}
                                                    />
                                                    {!validation.email && (
                                                        <div className="validation-error">
                                                            {validationMessages.email}
                                                        </div>
                                                    )}
                                                </div>
                                            </div>
                                            <div className="form-group">
                                                <label htmlFor="phone">Mobile number <span className="required">*</span></label>
                                                <input 
                                                    type="tel" 
                                                    id="phone" 
                                                    name="phone" 
                                                    value={formData.phone}
                                                    onChange={handleChange}
                                                    required 
                                                    className={!validation.phone ? 'is-invalid' : ''}
                                                />
                                                {!validation.phone && (
                                                    <div className="validation-error">
                                                        {validationMessages.phone}
                                                    </div>
                                                )}
                                            </div>
                                            <div className="form-group">
                                                <label htmlFor="message">Message <span className="required">*</span></label>
                                                <textarea 
                                                    id="message" 
                                                    name="message" 
                                                    rows="5" 
                                                    value={formData.message}
                                                    onChange={handleChange}
                                                    required
                                                ></textarea>
                                            </div>
                                            <button 
                                                type="submit" 
                                                className="submit-btn"
                                                disabled={isSubmitting}
                                            >
                                                {isSubmitting ? (
                                                    <>
                                                        <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                                                        Sending...
                                                    </>
                                                ) : (
                                                    'Send Message'
                                                )}
                                            </button>
                                        </form>
                                    )}
                                    <div className="dot-pattern"></div>
                                </div>
                            </div>
                        </div>
                    </section>

                    <section className="map-section">
                        <div className="container">
                            <div className="map-container">
                                <div className="map-frame">
                                    <iframe 
                                        src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3770.657046950237!2d72.90034287379168!3d19.076561151637154!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x3be7c87c8e152701%3A0x90dd15f8cbb8ec5e!2sMumbai%2C%20Maharashtra!5e0!3m2!1sen!2sin!4v1656531021397!5m2!1sen!2sin" 
                                        width="100%" 
                                        height="450" 
                                        style={{ border: 0 }} 
                                        allowFullScreen="" 
                                        loading="lazy" 
                                        referrerPolicy="no-referrer-when-downgrade"
                                        title="Google Maps Location"
                                    ></iframe>
                                </div>
                            </div>
                        </div>
                    </section>
                </div>
                
                <Footer />
                <BackToTop />
                <WhatsappButton message="Hello, I have a question about LSQ Stays" />
            </div>
        </div>
    );
}

export default Contact;